import { v4 as uuidv4 } from 'uuid';
import {
  changeValidated,
  enableSetRequest,
  MAX_REQUEST_DEAINFOs,
  RequestAction,
  selectedDeaInfoSize,
  stateRequireDocument,
  validateDeaNum,
  validateMaxRequestDeaInfos,
} from '.';
import {
  deaNumNotFound,
  ExtendedRoleCode,
  IApiCallStatus,
  IDeaEditMode,
  IDeaInfo,
  IDocument,
  IError,
  IFeature,
  ISaveRequestInput,
  ISaveRequestOutput,
  SecondaryRole,
  secondaryRoles,
} from '../common';
import {
  compose as lcompose,
  filter as lfilter,
  map as lmap,
  orderBy as lorderBy,
  find as lfind,
  getOr as lgetOr,
  head as lhead,
  compact as lcompact,
  flow,
} from 'lodash/fp';
import { assocPath, path } from 'rambda';

export interface IRequestState {
  request: ISaveRequestInput;
  file?: File;
  requests: ISaveRequestOutput[];
  apiCallStatus: IRequestApiCallStatus[];
  error?: IError;
  feedback: string;
  changeValidated: boolean;
  setRequestDisabled: boolean;
}

export const initRequestState: IRequestState = {
  requests: [],
  request: {
    reqType: 'ENROLL',
    comments: null,
    otherComments: null,
    role: '',
    secondaryRole: null,
    thirdRole: null,
    status: 'NEW',
    deaInfos: [],
    requestDocuments: [],
  },
  apiCallStatus: [],
  feedback: '',
  changeValidated: false,
  setRequestDisabled: false,
};

export interface IRequestApiCallStatus {
  type:
    | 'SaveRequest'
    | 'SaveFile'
    | 'ConfirmPOALetterOnFile'
    | 'ConfirmEmploymentVerification'
    | 'ConfirmSubmitRequest';
  apiCallStatus: IApiCallStatus;
}

export const requestReducer =
  (feature: IFeature) =>
  (state: IRequestState, { type, payload }: RequestAction): IRequestState => {
    // console.log(type, payload, state);
    switch (type) {
      case 'DisableSetRequest':
        return { ...state, setRequestDisabled: payload as boolean };

      case 'SetRequest':
        return enableSetRequest(state)
          ? lcompose(
              assocPath<IRequestState>('changeValidated', true),
              assocPath<IRequestState>('request', payload as ISaveRequestInput)
            )(state)
          : state;

      case 'SelectDeaInfo':
        const keysToSelect = validateMaxRequestDeaInfos(MAX_REQUEST_DEAINFOs)(
          selectedDeaInfoSize(state.request.deaInfos)
        )(payload.checked)([payload.key]);

        return lcompose(
          assocPath<IRequestState>('setRequestDisabled', true),
          assocPath<IRequestState>('changeValidated', false),
          assocPath<IRequestState>(
            'request.deaInfos',
            lcompose(
              selectedDeaInfoSize(state.request.deaInfos) +
                keysToSelect.length >=
                MAX_REQUEST_DEAINFOs && payload.checked
                ? lmap<IDeaEditMode, IDeaEditMode>(a =>
                    a.selected ? a : { ...a, disabled: true }
                  )
                : lmap<IDeaEditMode, IDeaEditMode>(a =>
                    a.selected ? a : { ...a, disabled: false }
                  ),
              lmap<IDeaEditMode, IDeaEditMode>(a =>
                keysToSelect.indexOf(a.key) === -1
                  ? a
                  : { ...a, selected: payload.checked }
              )
            )(state.request.deaInfos)
          )
        )(state);

      case 'SelectAllDeaInfo':
        const keysToSelectAll = validateMaxRequestDeaInfos(
          MAX_REQUEST_DEAINFOs
        )(selectedDeaInfoSize(state.request.deaInfos))(payload.checked)(
          payload.keys
        );

        return lcompose(
          assocPath<IRequestState>('setRequestDisabled', true),
          assocPath<IRequestState>('changeValidated', false),
          assocPath<IRequestState>(
            'request.deaInfos',
            lcompose(
              selectedDeaInfoSize(state.request.deaInfos) +
                keysToSelectAll.length >=
                MAX_REQUEST_DEAINFOs && payload.checked
                ? lmap<IDeaEditMode, IDeaEditMode>(a =>
                    a.selected ? a : { ...a, disabled: true }
                  )
                : lmap<IDeaEditMode, IDeaEditMode>(a =>
                    a.selected ? a : { ...a, disabled: false }
                  ),
              lmap<IDeaEditMode, IDeaEditMode>(a => ({
                ...a,
                selected:
                  keysToSelectAll.indexOf(a.key) === -1
                    ? a.selected
                    : payload.checked,
              }))
            )(state.request.deaInfos)
          )
        )(state);

      // save request

      case 'SaveRequest':
        return {
          ...state,
          request: assocPath<ISaveRequestInput>(
            'status',
            payload as string
          )(state.request),
          apiCallStatus:
            ['NEW', 'CANCEL'].indexOf(payload as string) !== -1
              ? [
                  {
                    type,
                    apiCallStatus: null,
                  },
                ]
              : // : includes(['POA', 'APO', 'POP'])(state.request.role)
                // ? [{ type: 'ConfirmPOALetterOnFile', apiCallStatus: null }]
                // : [{ type: 'ConfirmSubmitRequest', apiCallStatus: null }],
                [{ type: 'ConfirmSubmitRequest', apiCallStatus: null }],
        };

      case 'ConfirmSubmitRequest':
        return {
          ...state,
          apiCallStatus: (payload as boolean)
            ? [
                {
                  type: 'SaveRequest',
                  apiCallStatus: null,
                },
              ]
            : [],
        };

      case 'ConfirmEmploymentVerificationSubmitRequest':
        return {
          ...state,
          apiCallStatus: (payload as boolean)
            ? [
                {
                  type: 'ConfirmEmploymentVerification',
                  apiCallStatus: null,
                },
              ]
            : [],
        };

      case 'ConfirmPOALetterOnFile':
        return {
          ...state,
          apiCallStatus: (payload as boolean)
            ? [
                {
                  type: 'ConfirmSubmitRequest',
                  apiCallStatus: null,
                },
              ]
            : [],
        };

      case 'SaveRequestSuccess':
        const saveRequestOutput = payload as ISaveRequestOutput;

        const requestDocuments = lcompose(
          lgetOr<ISaveRequestOutput, 'requestDocuments', IDocument[]>(
            [],
            'requestDocuments'
          ),
          lfind<ISaveRequestOutput>({ requestId: saveRequestOutput.requestId })
        )(state.requests);

        return {
          ...state,
          setRequestDisabled: false,
          apiCallStatus: stateRequireDocument(state)
            ? [
                {
                  type: 'SaveFile',
                  apiCallStatus: null,
                },
                {
                  type: 'SaveRequest',
                  apiCallStatus: true,
                },
              ]
            : [
                {
                  type: 'SaveRequest',
                  apiCallStatus: true,
                },
              ],

          request:
            (feature.sailpoint && state.request.status === 'SUBMITTED') ||
            state.request.reqType === 'REVOKE'
              ? { ...state.request, requestId: saveRequestOutput.requestId }
              : state.request.reqType === 'RENEWAL'
              ? {
                  ...state.request,
                  requestId: saveRequestOutput.requestId,
                  deaInfos: [
                    ...lcompose(
                      lmap<IDeaInfo, IDeaEditMode>(
                        lcompose(
                          (a: IDeaEditMode) => ({
                            ...a,

                            role: flow(
                              lfind<IDeaEditMode>({ deaNum: a.deaNum }),
                              path<string>('role')
                            )(state.request.deaInfos),

                            expDt: flow(
                              lfind<IDeaEditMode>({ deaNum: a.deaNum }),
                              path<Date>('expDt')
                            )(state.request.deaInfos),
                          }),
                          (a: IDeaEditMode) => ({
                            ...a,
                            key: uuidv4(),
                          }),
                          assocPath('editMode', 'Select'),
                          assocPath('selected', true)
                        )
                      ),
                      lorderBy(['msgText'], ['asc'])
                    )(saveRequestOutput.deaInfos),
                    ...lfilter<IDeaEditMode>(({ deaNum }) =>
                      deaNumNotFound({ deaNum })(saveRequestOutput.deaInfos)
                    )(state.request.deaInfos),
                  ],
                }
              : {
                  ...saveRequestOutput,

                  role: state.request.role,
                  secondaryRole: state.request.secondaryRole,
                  thirdRole: state.request.thirdRole,

                  comments: state.request.comments,
                  otherComments: state.request.otherComments,
                  deaInfos: lcompose(
                    lmap<IDeaInfo, IDeaEditMode>(
                      lcompose(
                        (a: IDeaEditMode) => ({ ...a, key: uuidv4() }),
                        assocPath('editMode', 'Read')
                      )
                    ),
                    lorderBy(['msgText'], ['asc'])
                  )(saveRequestOutput.deaInfos),
                  requestDocuments,
                },

          changeValidated: changeValidated(saveRequestOutput),
        };

      case 'SaveRequestError':
        return {
          ...state,
          setRequestDisabled: false,
          apiCallStatus: [
            {
              type: 'SaveRequest',
              apiCallStatus: false,
            },
          ],
          error: payload as IError,
        };

      // type

      case 'SetType':
        return lcompose(
          assocPath<IRequestState>('apiCallStatus', []),
          assocPath<IRequestState>('setRequestDisabled', false),
          assocPath<IRequestState>('changeValidated', false),
          assocPath<IRequestState>('file', void 0),
          assocPath<IRequestState>('request.requestDocuments', []),
          assocPath<IRequestState>('request.comments', null),
          assocPath<IRequestState>('request.role', ''),
          assocPath<IRequestState>('request.status', 'NEW'),
          assocPath<IRequestState>('request.deaInfos', []),
          assocPath<IRequestState>('request.reqType', payload as string)
        )(state);

      // role

      case 'SetRole':
        return lcompose(
          assocPath<IRequestState>('setRequestDisabled', true),
          assocPath<IRequestState>('changeValidated', false),
          assocPath<IRequestState>('file', void 0),
          assocPath<IRequestState>('request.requestDocuments', []),
          assocPath<IRequestState>('request.thirdRole', null),
          assocPath<IRequestState>(
            'request.secondaryRole',
            lcompose(
              lgetOr(null, 'value'),
              lhead,
              lfilter<SecondaryRole>({
                parent: payload as ExtendedRoleCode,
                level: 1,
                default: true,
              })
            )(secondaryRoles)
          ),
          assocPath<IRequestState>('request.role', payload as string)
        )(state);

      case 'SetSecondaryRole':
        return lcompose(
          assocPath<IRequestState>('changeValidated', false),
          assocPath<IRequestState>('file', void 0),
          assocPath<IRequestState>('request.requestDocuments', []),
          assocPath<IRequestState>(
            'request.thirdRole',
            lcompose(
              lgetOr(null, 'value'),
              lhead,
              lfilter<SecondaryRole>({
                parent: payload as ExtendedRoleCode,
                level: 2,
                default: true,
              })
            )(secondaryRoles)
          ),
          assocPath<IRequestState>('request.secondaryRole', payload as string)
        )(state);

      // justification

      case 'SetJustification':
        return lcompose(
          assocPath<IRequestState>('setRequestDisabled', true),
          assocPath<IRequestState>('changeValidated', false),
          assocPath<IRequestState>('request.comments', payload as string)
        )(state);

      case 'SetOtherComments':
        return lcompose(
          assocPath<IRequestState>('changeValidated', false),
          assocPath<IRequestState>('request.otherComments', payload as string)
        )(state);

      // deaNum

      case 'CreateDeaNum':
        return lcompose(
          assocPath<IRequestState>('setRequestDisabled', true),
          assocPath<IRequestState>('changeValidated', false),
          assocPath<IRequestState>(
            'feedback',
            `DEA registration number ${(payload as IDeaEditMode).deaNum} added`
          ),
          assocPath<IRequestState>(
            'request.deaInfos',

            lcompose(
              lorderBy(['msgText'], ['asc']),
              lcompact
            )([
              ...state.request.deaInfos,

              validateDeaNum(state.request.deaInfos)(
                payload as IDeaEditMode
              ) === 'Duplicate DEA Registration Number'
                ? void 0
                : {
                    ...(payload as IDeaEditMode),
                    msgText: validateDeaNum(state.request.deaInfos)(
                      payload as IDeaEditMode
                    ),
                  },
            ])
          )
        )(state);

      case 'CreateDeaNums':
        return lcompose(
          assocPath<IRequestState>('setRequestDisabled', true),
          assocPath<IRequestState>('changeValidated', false),
          assocPath<IRequestState>('request.deaInfos', [
            ...state.request.deaInfos,
            ...lmap<string[], IDeaEditMode>(a => ({
              key: uuidv4(),
              deaNum: a[0],
              editMode: 'Read',
              msgCode: null,
              msgText: null,
              serialNumber: null,
            }))(payload as string[][]),
          ])
        )(state);

      case 'EdittingDeaNum':
        return lcompose(
          assocPath<IRequestState>('setRequestDisabled', true),
          assocPath<IRequestState>(
            'request.deaInfos',
            lmap<IDeaEditMode, IDeaEditMode>(item =>
              item.key === (payload as string)
                ? { ...item, editMode: 'Update' }
                : { ...item, editMode: 'Read' }
            )(state.request.deaInfos)
          )
        )(state);

      case 'UpdateDeaNum':
        return lcompose(
          assocPath<IRequestState>('setRequestDisabled', true),
          assocPath<IRequestState>('changeValidated', false),
          assocPath<IRequestState>(
            'request.deaInfos',

            lcompose(
              lorderBy(['msgText'], ['asc']),
              lmap<IDeaEditMode, IDeaEditMode>(item =>
                item.key === (payload as IDeaEditMode).key
                  ? {
                      ...(payload as IDeaEditMode),
                      msgText: validateDeaNum(state.request.deaInfos)(
                        payload as IDeaEditMode
                      ),
                    }
                  : item
              )
            )(state.request.deaInfos)
          )
        )(state);

      case 'DeleteDeaNum':
        return lcompose(
          assocPath<IRequestState>('setRequestDisabled', true),
          assocPath<IRequestState>('changeValidated', false),
          assocPath<IRequestState>(
            'request.deaInfos',
            lfilter<IDeaEditMode>(item => item.key !== (payload as string))(
              state.request.deaInfos
            )
          )
        )(state);

      // file

      case 'SetFile':
        return { ...state, file: payload as File, setRequestDisabled: true };

      case 'SaveFileSuccess':
        return {
          ...state,
          apiCallStatus: [
            {
              type: 'SaveFile',
              apiCallStatus: true,
            },
            {
              type: 'SaveRequest',
              apiCallStatus: true,
            },
          ],
        };

      case 'SaveFileError':
        return {
          ...state,
          apiCallStatus: [
            {
              type: 'SaveFile',
              apiCallStatus: false,
            },
            {
              type: 'SaveRequest',
              apiCallStatus: true,
            },
          ],
        };
    }
  };
